import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RotateCcw, Send, Undo2 } from 'lucide-react'
import type { SiteDocCountersignRequestView, SiteDocDetail, SiteDocProjectContact } from '@symphenia/shared-types'
import { bestaetige } from '@/lib/dialoge'
import { fehlertext } from '@/lib/fehlertext'
import { fmtDate, fmtDateTime } from '@/lib/format-date'
import { fetchCountersignRequests, fetchProjectChoices, resendCountersignRequest, revokeCountersignRequest } from '../api/site-docs.api'
import { GegenzeichnungSendenDialog } from './GegenzeichnungSenden'

/** Kontakte des Projekts eines Dokuments — aus der Projektauswahl; fehlt das Projekt dort, bleibt die Eingabe frei. */
export async function kontakteFuer(d: { projectId: string | null; projectRefId: string | null }): Promise<SiteDocProjectContact[]> {
  const id = d.projectId ?? d.projectRefId
  if (!id) return []
  try {
    const r = await fetchProjectChoices()
    return r.data.find((p) => p.id === id)?.contacts ?? []
  } catch {
    return []
  }
}

/**
 * AP-48 6.3, 6.4 — **Gegenzeichnungen und Einwände** am Detail eines freigegebenen Dokuments: die Sammel-Anfragen,
 * in denen es steht, mit Empfänger, Frist und dem Stand **dieses** Dokuments (gezeichnet, Einwand mit Text, Name
 * und Zeitpunkt, offen); „Zurückziehen" und „Neu senden"; „Zur Gegenzeichnung senden" für ein freigegebenes
 * Dokument (Recht `sitedocs.document.send`).
 */
export function GegenzeichnungStand({ dok }: { dok: SiteDocDetail }) {
  const { t } = useTranslation()
  const [anfragen, setAnfragen] = useState<SiteDocCountersignRequestView[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [senden, setSenden] = useState<SiteDocProjectContact[] | null>(null)
  const [gesendet, setGesendet] = useState<string | null>(null)
  const darf = dok.can.send

  const lade = useCallback(async () => {
    try {
      setAnfragen(await fetchCountersignRequests({ documentId: dok.id }))
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }, [dok.id, t])

  useEffect(() => {
    void lade()
  }, [lade])

  const tue = async (frageText: string, ok: string, f: () => Promise<unknown>) => {
    if (!(await bestaetige(frageText, { ok }))) return
    setFehler(null)
    try {
      await f()
      await lade()
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }

  return (
    <div className="space-y-3 text-sm">
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{fehler}</p>}
      {dok.status === 'FINAL' && darf && (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-sm hover:bg-muted" onClick={async () => setSenden(await kontakteFuer(dok))}>
            <Send className="h-4 w-4" /> {t('siteDocs.countersign.sendTitle')}
          </button>
          {gesendet && <span className="text-muted-foreground">{gesendet}</span>}
        </div>
      )}
      {anfragen && anfragen.length === 0 && <p className="text-muted-foreground">{t('siteDocs.countersign.none')}</p>}
      <ul className="space-y-2">
        {anfragen?.map((a) => {
          const item = a.items.find((i) => i.documentId === dok.id)
          const offen = !a.revokedAt && !a.done
          return (
            <li key={a.id} className="space-y-1 rounded-md border px-3 py-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{a.name ? `${a.name} · ${a.email}` : a.email}</span>
                <span className="text-xs text-muted-foreground">
                  {t(`siteDocs.roles.${a.role}`)}
                  {a.function ? ` · ${a.function}` : ''}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {[
                  a.sentAt ? t('siteDocs.countersign.sentAt', { datum: fmtDateTime(a.sentAt) }) : t('siteDocs.countersign.notSent'),
                  a.openedAt ? t('siteDocs.countersign.openedAt', { datum: fmtDateTime(a.openedAt) }) : null,
                  a.remindedAt.length ? t('siteDocs.countersign.remindedAt', { daten: a.remindedAt.map((d) => fmtDate(d)).join(', ') }) : null,
                  a.revokedAt ? t('siteDocs.countersign.revokedAt', { datum: fmtDateTime(a.revokedAt) }) : a.expired ? t('siteDocs.countersign.expiredAt', { datum: fmtDate(a.expiresAt) }) : t('siteDocs.countersign.validUntil', { datum: fmtDate(a.expiresAt) }),
                  a.items.length > 1 ? t('siteDocs.countersign.documents', { count: a.items.length }) : null,
                  t('siteDocs.countersign.by', { name: a.createdByName }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {item?.outcome === 'SIGNED' && <p className="text-emerald-700 dark:text-emerald-300">{t('siteDocs.countersign.signedBy', { name: item.answeredName ?? '', datum: item.answeredAt ? fmtDateTime(item.answeredAt) : '' })}</p>}
              {item?.outcome === 'OBJECTED' && (
                <div className="rounded-md border-l-4 border-amber-500 bg-amber-50 px-2 py-1 dark:bg-amber-900/30">
                  <p className="font-medium">{t('siteDocs.countersign.objectionBy', { name: item.answeredName ?? a.email, datum: item.answeredAt ? fmtDateTime(item.answeredAt) : '' })}</p>
                  <p className="whitespace-pre-line">{item.objection}</p>
                </div>
              )}
              {item && !item.outcome && !a.revokedAt && <p className="text-amber-700 dark:text-amber-300">{t('siteDocs.countersign.open')}</p>}
              {darf && offen && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <button type="button" className="inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs" onClick={() => void tue(t('siteDocs.countersign.resendConfirm'), t('siteDocs.countersign.resend'), () => resendCountersignRequest(a.id))}>
                    <RotateCcw className="h-3.5 w-3.5" /> {t('siteDocs.countersign.resend')}
                  </button>
                  <button type="button" className="inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs text-red-700" onClick={() => void tue(t('siteDocs.countersign.revokeConfirm'), t('siteDocs.countersign.revoke'), () => revokeCountersignRequest(a.id))}>
                    <Undo2 className="h-3.5 w-3.5" /> {t('siteDocs.countersign.revoke')}
                  </button>
                </div>
              )}
            </li>
          )
        })}
      </ul>
      {senden && (
        <GegenzeichnungSendenDialog
          dokumente={[dok]}
          kontakte={senden}
          onClose={() => setSenden(null)}
          onGesendet={(a) => {
            setSenden(null)
            setGesendet(t('siteDocs.countersign.sentTo', { email: a.email }))
            void lade()
          }}
        />
      )}
    </div>
  )
}
